import { useMemo, useState } from 'react'
import { initialsOf } from './bits.tsx'
import {
  filtrarPessoas,
  lerRecentes,
  ordenarPessoas,
  quantasMostrar,
  registrarUso,
  type Pessoa,
} from './pessoas-recentes.ts'

/**
 * A lista de pessoas do Item de Trabalho — membros e responsável, num lugar só
 * (SPEC-20260923-2315).
 *
 * Existia três vezes, copiada: no painel de membros do item, na linha de
 * checklist e nos controles do filho. Foi essa cópia que deixou a estrela do
 * responsável fora de um dos controles por dois dias, até o usuário reclamar
 * que "não encontrou essa opção". Uma lista só resolve isso de uma vez.
 *
 * Abre CURTA: quem já está marcado, mais os últimos que esta pessoa usou. O
 * resto aparece ao digitar. Em workspace grande, abrir a lista inteira obriga
 * a procurar com o olho o mesmo punhado de nomes toda vez.
 */
export function ListaDePessoas({
  pessoas,
  ehMembro,
  responsavelId,
  onAlternarMembro,
  onDefinirResponsavel,
  onFechar,
  tamanhoDoAvatar = 22,
}: {
  pessoas: readonly Pessoa[]
  ehMembro: (id: string) => boolean
  responsavelId: string | null
  onAlternarMembro: (id: string) => void
  /** `null` tira o responsável (clicar na estrela de quem já é). */
  onDefinirResponsavel: (id: string | null) => void
  /**
   * Fecha a lista depois de marcar pelo NOME (pedido 2026-09-23 23:44).
   *
   * Só o nome fecha. A estrela deixa aberto de propósito: definir o
   * responsável costuma vir junto de incluir quem mais acompanha, e fechar ali
   * obrigaria a reabrir a lista no meio do mesmo pensamento.
   */
  onFechar?: () => void
  tamanhoDoAvatar?: number
}) {
  const [busca, setBusca] = useState('')
  /* Lido uma vez por abertura: reordenar sob os dedos enquanto a pessoa marca
     faria a linha fugir do ponteiro. */
  const recentes = useMemo(() => lerRecentes(), [])

  const marcados = useMemo(
    () => new Set(pessoas.filter((p) => ehMembro(p.id) || p.id === responsavelId).map((p) => p.id)),
    [pessoas, ehMembro, responsavelId],
  )
  const ordenadas = useMemo(
    () => ordenarPessoas(pessoas, marcados, recentes),
    [pessoas, marcados, recentes],
  )
  const achadas = filtrarPessoas(ordenadas, busca)
  const buscando = busca.trim() !== ''
  const limite = quantasMostrar(marcados.size)
  const visiveis = buscando ? achadas : achadas.slice(0, limite)
  const escondidas = buscando ? 0 : achadas.length - visiveis.length

  const usar = (id: string, acao: () => void) => {
    registrarUso(id)
    acao()
  }
  /** Marcar pelo nome resolve e fecha; a estrela continua com a lista aberta. */
  const marcarEFechar = (id: string) => {
    usar(id, () => onAlternarMembro(id))
    onFechar?.()
  }

  return (
    <>
      <input
        className="wi-panel__search"
        type="search"
        autoFocus
        value={busca}
        placeholder="Buscar pessoa…"
        aria-label="Buscar pessoa"
        onChange={(e) => setBusca(e.target.value)}
      />
      {visiveis.length === 0 ? (
        <p className="wi-cl__menuvazio">Ninguém com esse nome.</p>
      ) : (
        visiveis.map((p) => {
          const membro = ehMembro(p.id)
          const responsavel = responsavelId === p.id
          return (
            <div key={p.id} className="wi-menu__linha">
              <button
                type="button"
                className={`wi-menu__estrela${responsavel ? ' wi-menu__estrela--on' : ''}`}
                aria-pressed={responsavel}
                aria-label={responsavel ? `Tirar ${p.name} de responsável` : `Tornar ${p.name} responsável`}
                title={responsavel ? 'Responsável — clique para tirar' : 'Tornar responsável'}
                onClick={() => usar(p.id, () => onDefinirResponsavel(responsavel ? null : p.id))}
              >
                <i className={`${responsavel ? 'fas' : 'far'} fa-star`} aria-hidden />
              </button>
              <button
                type="button"
                className={`wi-menu__opt${membro ? ' wi-menu__opt--on' : ''}`}
                aria-pressed={membro}
                title={membro ? `Tirar ${p.name} dos membros` : `Incluir ${p.name} como membro`}
                onClick={() => marcarEFechar(p.id)}
              >
                <span
                  className="wi-chip-member"
                  style={{ width: tamanhoDoAvatar, height: tamanhoDoAvatar, fontSize: 9 }}
                >
                  {initialsOf(p.name)}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.name}
                </span>
              </button>
              {/* mesma ação da linha, por isso fora da árvore de acessibilidade */}
              <button
                type="button"
                className={`wi-menu__visto${membro ? ' wi-menu__visto--on' : ''}`}
                tabIndex={-1}
                aria-hidden
                title={membro ? 'Membro — clique para tirar' : 'Incluir como membro'}
                onClick={() => marcarEFechar(p.id)}
              >
                <i className="fas fa-check" aria-hidden />
              </button>
            </div>
          )
        })
      )}
      {escondidas > 0 ? (
        /* diz que há mais gente e o que fazer para chegar nela — uma lista
           cortada sem aviso parece lista completa */
        <p className="wi-cl__menuvazio">
          {escondidas === 1 ? 'Mais 1 pessoa' : `Mais ${escondidas} pessoas`} — digite para buscar
        </p>
      ) : null}
    </>
  )
}
